/**
 * Transformers API Response Mappers
 *
 * Each function handles a specific API endpoint response format.
 * This ensures precise transformation based on actual API contracts.
 */
import { reverseMap, transformArray, transformKeys } from '@/shared/mappers/base.mapper';

import type {
  ExecutionDetails,
  ExecutionLog,
  IntegrationType,
  Transformer,
  TransformerVersion,
  Trigger,
  TriggerDetailsResponse,
} from '@/types';
import type { ScheduleSummary, TriggerDetail as ScheduleTriggerDetail } from '@/types/schedule';

// =============================================================================
// Field Mappings (Backend → Frontend)
// =============================================================================

/**
 * Transformer field mapping (for GET /transformers, GET /transformer/{baseId})
 */
const TRANSFORMER_FIELDS = {
  Id: 'id',
  BaseId: 'baseId',
  Name: 'name',
  Version: 'version',
  Description: 'description',
  Environ: 'environ',
  LastModified: 'lastModified',
  LastModifiedBy: 'lastModifiedBy',
  CreatedDate: 'createdDate',
  Deployed: 'deployed',
  GeneratedFromPiTypeId: 'generatedFromPiTypeId',
  MaxDataProcTime: 'maxDataProcTime',
  ProcessingType: 'processingType',
  Config: 'config',
  // Preserve camelCase fields: appId, tenantId, state
};

/**
 * TransformerVersion field mapping (for GET /transformer/versions/{baseId})
 */
const TRANSFORMER_VERSION_FIELDS = {
  Id: 'id',
  Version: 'version',
  BaseId: 'baseId',
  Name: 'name',
  Config: 'config',
  ConfigJson: 'configJson',
  LanguageVersion: 'languageVersion',
  LastModified: 'lastModified',
  LastModifiedBy: 'lastModifiedBy',
  CreatedDate: 'createdDate',
  Deployed: 'deployed',
  Comment: 'comment',
  Description: 'description',
  Environ: 'environ',
  ProcessingType: 'processingType',
  MaxDataProcTime: 'maxDataProcTime',
};

/**
 * IntegrationType field mapping (for GET /integration-types)
 */
const INTEGRATION_TYPE_FIELDS = {
  Id: 'id',
  Name: 'name',
  Description: 'description',
  Category: 'category',
  // Preserve camelCase: icon, color, tags
};

/**
 * ExecutionLog field mapping (for GET /logs/transformers/{baseId})
 */
const EXECUTION_LOG_FIELDS = {
  Id: 'id',
  TransId: 'transId', // Transformer ID (available in workspace executions)
  Name: 'name',
  BaseId: 'baseId',
  Version: 'version',
  ExecutionMetadata: 'executionMetadata',
  // Preserve camelCase: executionGUID, externalExecutionId, startTime, finishTime,
  // completed, succeeded, statusText, timer, recordsRead, environmentId,
  // errorMessage, triggerType, recipeVersionNumber, running
};

/**
 * ExecutionMetadata field mapping (nested in execution log)
 */
const EXECUTION_METADATA_FIELDS = {
  BaseId: 'baseId',
  GeneratedFromPi: 'generatedFromPi',
  LanguageVersion: 'languageVersion',
  RecipeName: 'recipeName',
  // Preserve camelCase fields
};

/**
 * ExecutionStep field mapping (for GET /logs/steps/externalExecutionId/{guid})
 */
const EXECUTION_STEP_FIELDS = {
  Statement: 'statement',
  Result: 'result',
  RecordsRead: 'recordsRead',
  Timer: 'timer',
  StepGuid: 'stepGuid',
  ExtendedProperties: 'extendedProperties',
  Succeeded: 'succeeded',
  Completed: 'completed',
  StartTime: 'startTime',
  SortOrder: 'sortOrder',
  ErrorMessage: 'errorMessage',
  StepAction: 'stepAction',
  StatusText: 'statusText',
  OnError: 'onError',
};

/**
 * Trigger field mapping (for GET /gettriggers, GET /getalltriggers)
 */
const TRIGGER_FIELDS = {
  Id: 'id',
  BaseId: 'baseId',
  TransBaseId: 'transBaseId',
  CronExpression: 'cronExpression',
  Frequency: 'frequency',
  IsSchedulesPaused: 'isSchedulesPaused',
  EnvironmentId: 'environmentId',
  NextRun: 'nextRun',
};

/**
 * TriggerSummary field mapping (for GET /getTriggerSummary)
 */
const TRIGGER_SUMMARY_FIELDS = {
  TransBaseId: 'transBaseId',
  BaseId: 'baseId',
  Name: 'name',
  TransformerName: 'transformerName',
  EnvironmentId: 'environmentId',
  EnvironmentName: 'environmentName',
  CronExpression: 'cronExpression',
  CronExplain: 'cronExplain',
  Frequency: 'frequency',
  IsSchedulesPaused: 'isSchedulesPaused',
  NextRun: 'nextRun',
  LastRun: 'lastRun',
  LastRunDuration: 'lastRunDuration',
  ExecutionStatus: 'executionStatus',
  ExternalExecutionId: 'externalExecutionId',
  ExecutionTime: 'executionTime',
  TotalExecutions: 'totalExecutions',
  SuccessCount: 'successCount',
  FailureCount: 'failureCount',
  TemplateName: 'templateName',
  DailySchedule: 'dailySchedule',
  RepeatingSchedule: 'repeatingSchedule',
};

/**
 * Deployment field mapping (for GET /{tenantId}/{appId}/deployments)
 */
const DEPLOYMENT_FIELDS = {
  BaseId: 'baseId',
  EnvironmentId: 'environmentId',
  // Backend json tag on TransformerDeploymentDTO.Version (not PascalCase)
  transformerVersion: 'version',
  LastModified: 'lastModified',
  // Preserve camelCase: transformerName, environmentName, baseId, environmentId, transId
};

/**
 * TriggerDetailsResponse field mapping (for GET /getTriggerDetails)
 */
const TRIGGER_DETAILS_RESPONSE_FIELDS = {
  TotalRecordCount: 'totalRecordCount',
  TriggerDetails: 'triggerDetails',
};

/**
 * TriggerDetail field mapping (nested in TriggerDetailsResponse)
 *
 * Verified against legacy dataProtect/src/shared/redux/actions/eventsActions.js:
 * The API returns camelCase fields for this endpoint. PascalCase mappings are
 * kept for safety in case the backend ever changes, but most fields pass through as-is.
 */
const TRIGGER_DETAIL_FIELDS = {
  ExecutionTime: 'executionTime',
  TransformerName: 'transformerName',
  EnvironmentName: 'environmentName',
  EnvironmentId: 'environmentId',
  BaseId: 'baseId',
  ExternalExecutionId: 'externalExecutionId',
  ExecutionStatus: 'executionStatus',
};

/**
 * ExecutionDetails field mapping (for GET /trans/executions/{transId})
 */
const EXECUTION_DETAILS_FIELDS = {
  Id: 'id',
  TransId: 'transId',
  State: 'state',
  StartTime: 'startTime',
  EndTime: 'endTime',
  InputData: 'inputData',
  OutputData: 'outputData',
  Logs: 'logs',
};

// =============================================================================
// Response Transformers (Backend → Frontend)
// =============================================================================

/**
 * Extract text value from XML-to-JSON objects
 * Handles cases where API returns {#text: "value"} instead of "value"
 */
function extractTextValue(value: unknown): unknown {
  if (value === null || value === undefined) return value;

  // Handle object with #text property (XML-to-JSON artifact)
  if (typeof value === 'object' && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    if ('#text' in obj) {
      return obj['#text'];
    }
  }

  return value;
}

/**
 * Transform GET /transformers response
 * Returns array of transformers for an app
 */
export const transformGetTransformersResponse = (response: unknown): Transformer[] => {
  return transformArray(response, (item) => {
    const transformed = transformKeys<Transformer>(item, TRANSFORMER_FIELDS, {
      parseJson: false, // Config should remain as-is, not auto-parsed
      recursive: true,
    });

    // Extract text values from XML-to-JSON artifacts
    if (transformed.name) {
      transformed.name = extractTextValue(transformed.name) as string;
    }
    if (transformed.description) {
      transformed.description = extractTextValue(transformed.description) as string;
    }

    return transformed;
  });
};

/**
 * Transform GET /transformer/{baseId} response
 * Returns single transformer details
 */
export const transformGetTransformerResponse = (response: unknown): Transformer => {
  return transformKeys<Transformer>(response, TRANSFORMER_FIELDS, {
    parseJson: false, // Config should remain as-is, not auto-parsed
    recursive: true,
  });
};

/**
 * Transform GET /transformer/versions/{baseId} response
 * Returns array of transformer versions
 */
export const transformGetTransformerVersionsResponse = (
  response: unknown
): TransformerVersion[] => {
  const result = transformArray(response, (item) => {
    const transformed = transformKeys<TransformerVersion>(item, TRANSFORMER_VERSION_FIELDS, {
      parseJson: false, // ConfigJson should remain as string, parsed by consumer
      recursive: true,
    });

    // Extract text values from XML-to-JSON artifacts
    if (transformed.name) {
      transformed.name = extractTextValue(transformed.name) as string;
    }
    if (transformed.comment) {
      transformed.comment = extractTextValue(transformed.comment) as string;
    }

    return transformed;
  });

  // Sort descending by version number (latest first) - matches legacy reducer behavior
  result.sort((a, b) => {
    const vA = Number(a.version) || 0;
    const vB = Number(b.version) || 0;
    return vB - vA;
  });

  return result;
};

/**
 * Transform GET /integration-types response
 * Returns array of available integration types
 */
export const transformGetIntegrationTypesResponse = (response: unknown): IntegrationType[] => {
  return transformArray(response, (item) =>
    transformKeys<IntegrationType>(item, INTEGRATION_TYPE_FIELDS, {
      parseJson: false,
      recursive: true,
    })
  );
};

/**
 * Transform GET /integration-types/adopted response
 * Returns array of adopted integration types for an app
 */
export const transformGetAdoptedIntegrationTypesResponse = (
  response: unknown
): IntegrationType[] => {
  return transformArray(response, (item) =>
    transformKeys<IntegrationType>(item, INTEGRATION_TYPE_FIELDS, {
      parseJson: false,
      recursive: true,
    })
  );
};

/**
 * Transform GET /logs/transformers/{baseId} response
 * Returns array of execution logs
 */
export const transformGetTransformerLogsResponse = (response: unknown): ExecutionLog[] => {
  return transformArray(response, (item) =>
    transformKeys<ExecutionLog>(item, EXECUTION_LOG_FIELDS, {
      parseJson: false,
      recursive: true,
    })
  );
};

/**
 * Transform GET /workspace-executions response
 * Returns array of execution logs across all transformers
 */
export const transformGetWorkspaceExecutionsResponse = (response: unknown): ExecutionLog[] => {
  return transformArray(response, (item) => {
    const transformed = transformKeys<ExecutionLog>(item, EXECUTION_LOG_FIELDS, {
      parseJson: false,
      recursive: false,
    });

    // Transform nested executionMetadata if present (includes RecipeName)
    if (transformed.executionMetadata) {
      transformed.executionMetadata = transformKeys(
        transformed.executionMetadata,
        EXECUTION_METADATA_FIELDS,
        { parseJson: false, recursive: false }
      );
    }

    return transformed;
  });
};

/**
 * Transform GET /logs/transformers/externalExecutionId/{guid} response
 * Returns array with single execution metadata
 */
export const transformGetExecutionMetadataResponse = (response: unknown): ExecutionLog[] => {
  return transformArray(response, (item) => {
    const transformed = transformKeys<ExecutionLog>(item, EXECUTION_LOG_FIELDS, {
      parseJson: false,
      recursive: false,
    });

    // Transform nested executionMetadata if present
    if (transformed.executionMetadata) {
      transformed.executionMetadata = transformKeys(
        transformed.executionMetadata,
        EXECUTION_METADATA_FIELDS,
        { parseJson: false, recursive: false }
      );
    }

    return transformed;
  });
};

/**
 * Transform GET /logs/steps/externalExecutionId/{guid} response
 * Returns array of execution step logs sorted by sortOrder (matching dataProtect reducer behavior).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Execution step data is dynamic
export const transformGetExecutionStepsResponse = (response: unknown): any[] => {
  const steps = transformArray(response, (item) =>
    transformKeys(item, EXECUTION_STEP_FIELDS, {
      parseJson: false,
      recursive: false,
    })
  );
  steps.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  return steps;
};

/**
 * Transform GET /gettriggers and GET /getalltriggers response
 * Returns array of trigger subscriptions
 */
export const transformGetTriggersResponse = (response: unknown): Trigger[] => {
  return transformArray(response, (item) =>
    transformKeys<Trigger>(item, TRIGGER_FIELDS, {
      parseJson: false,
      recursive: true,
    })
  );
};

/**
 * Transform GET /getTriggerSummary response
 * Returns array of trigger execution summaries
 */
export const transformGetTriggerSummaryResponse = (response: unknown): ScheduleSummary[] => {
  return transformArray(response, (item) =>
    transformKeys<ScheduleSummary>(item, TRIGGER_SUMMARY_FIELDS, {
      parseJson: false,
      recursive: true,
    })
  );
};

/**
 * Transform GET /getTriggerDetails response
 * Returns trigger details with nested array of execution details
 */
export const transformGetTriggerDetailsResponse = (response: unknown): TriggerDetailsResponse => {
  const transformed = transformKeys<TriggerDetailsResponse>(
    response,
    TRIGGER_DETAILS_RESPONSE_FIELDS,
    {
      parseJson: false,
      recursive: false, // Don't transform nested array yet
    }
  );

  // Transform nested triggerDetails array
  if (transformed.triggerDetails && Array.isArray(transformed.triggerDetails)) {
    transformed.triggerDetails = transformArray(transformed.triggerDetails, (item) =>
      transformKeys<ScheduleTriggerDetail>(item, TRIGGER_DETAIL_FIELDS, {
        parseJson: false,
        recursive: false,
      })
    );
  }

  return transformed;
};

/**
 * Transform GET /trans/executions/{transId} response
 * Returns detailed execution information including input/output data and logs
 */
export const transformGetExecutionDetailsResponse = (response: unknown): ExecutionDetails => {
  return transformKeys<ExecutionDetails>(response, EXECUTION_DETAILS_FIELDS, {
    parseJson: true, // InputData, OutputData, Logs may contain JSON
    recursive: true,
  });
};

/**
 * Deployment type for getAppDeployments response
 */
export interface Deployment {
  baseId: string;
  environmentId: string;
  transformerName: string;
  environmentName: string;
  version: string;
  lastModified?: string;
}

/**
 * Transform GET /{tenantId}/{appId}/deployments response
 * Returns array of deployments for schedule creation and version lookup
 */
export const transformGetAppDeploymentsResponse = (response: unknown): Deployment[] => {
  return transformArray(response, (item) => {
    const transformed = transformKeys<Deployment>(item, DEPLOYMENT_FIELDS, {
      parseJson: false,
      recursive: false,
    });
    // Ensure required string fields have defaults
    transformed.baseId = transformed.baseId || '';
    transformed.environmentId = transformed.environmentId || '';
    transformed.transformerName = transformed.transformerName || '';
    transformed.environmentName = transformed.environmentName || '';
    const rawVersion = transformed.version;
    transformed.version =
      rawVersion !== undefined && rawVersion !== null && String(rawVersion) !== ''
        ? String(rawVersion)
        : '1';
    return transformed;
  });
};

// =============================================================================
// Request Transformers (Frontend → Backend)
// =============================================================================

/**
 * Transform POST/PUT transformer request body
 * Converts frontend format to backend format
 */
export const transformSaveTransformerRequest = (data: Partial<Transformer>): unknown => {
  const reverseFields = reverseMap(TRANSFORMER_FIELDS);
  return transformKeys(data, reverseFields, {
    parseJson: false,
    recursive: true,
  });
};

/**
 * Transform POST transformer version request body
 */
export const transformSaveTransformerVersionRequest = (
  data: Partial<TransformerVersion>
): unknown => {
  const reverseFields = reverseMap(TRANSFORMER_VERSION_FIELDS);
  return transformKeys(data, reverseFields, {
    parseJson: false,
    recursive: true,
  });
};
